import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useSpoilers, type UserSpoilerPreference } from '@/context/SpoilerContext';
import { Search, Flame, Menu, X, ShieldAlert, Shield, ShieldCheck, ChevronDown } from 'lucide-react';

interface HeaderProps {
  onOpenSearch: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onOpenSearch }) => {
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSpoilerMenuOpen, setIsSpoilerMenuOpen] = useState(false);
  const { preference, setPreference } = useSpoilers();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSpoilerMenuOpen(false);
  }, [location]);

  // Global keydown for Search modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenSearch();
      } else if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        onOpenSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpenSearch]);

  const navItems = [
    { label: 'Welt', href: '/world' },
    { label: 'Story', href: '/story' },
    { label: 'Charaktere', href: '/characters' },
    { label: 'Bestiarium', href: '/bestiary' },
    { label: 'Dungeons', href: '/dungeons' },
    { label: 'Items & Sets', href: '/items' },
    { label: 'Quests', href: '/quests' }
  ];

  const spoilerLabels: Record<UserSpoilerPreference, string> = {
    none: 'Spoiler: Geschützt',
    minor: 'Spoiler: Teils aktiv',
    all: 'Spoiler: Offen'
  };

  return (
    <header className="sticky top-0 z-50 bg-[#08090d]/90 backdrop-blur-xl border-b border-white/[0.07] transition-all">
      {/* Skip to content accessibility link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:bg-[#d4af37] focus:text-black focus:font-bold focus:rounded-md focus:shadow-lg"
      >
        Zum Hauptinhalt springen
      </a>

      <div className="editorial-container">
        <div className="flex items-center justify-between h-20 gap-6">
          {/* Brand Identity */}
          <Link href="/" className="flex items-center gap-3.5 shrink-0 group">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1a1c24] to-[#10121a] border border-[#d4af37]/40 flex items-center justify-center text-[#fae082] shadow-[0_0_15px_rgba(212,175,55,0.15)] group-hover:border-[#fae082] group-hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all">
              <Flame className="w-5 h-5 text-amber-400 group-hover:scale-110 transition-transform" />
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-black text-xl tracking-wider text-white group-hover:text-amber-200 transition-colors">
                DUNGEON DUDES
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#a9a396] -mt-0.5">
                DIGITALES KOMPENDIUM
              </span>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2" aria-label="Hauptnavigation">
            {navItems.map((item) => {
              const isActive = location === item.href || (item.href !== '/' && location.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`relative px-3.5 py-2 text-sm font-medium tracking-wide transition-all rounded-md ${
                    isActive
                      ? 'text-white font-semibold'
                      : 'text-[#a9a396] hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  <span>{item.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-3.5 right-3.5 h-[2px] bg-gradient-to-r from-amber-400 to-[#fae082] rounded-full shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right Actions: Search + Spoiler Settings */}
          <div className="flex items-center gap-3">
            {/* Sleek Command Bar Search Trigger */}
            <button
              onClick={onOpenSearch}
              aria-label="Kompendium durchsuchen"
              className="flex items-center gap-3 px-3.5 py-2 bg-[#10121a] hover:bg-[#161922] border border-white/[0.08] hover:border-amber-500/40 text-sm text-[#a9a396] hover:text-white rounded-lg transition-all cursor-pointer shadow-sm group"
            >
              <Search className="w-4 h-4 text-amber-400/90 group-hover:scale-110 transition-transform" />
              <span className="hidden sm:inline font-normal">Suchen...</span>
              <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[11px] font-mono font-medium bg-[#181c26] border border-white/10 rounded text-[#8f96a3]">
                ⌘K
              </kbd>
            </button>

            {/* Spoiler Control Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsSpoilerMenuOpen(!isSpoilerMenuOpen)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer ${
                  preference === 'none'
                    ? 'bg-[#10121a] border-white/[0.08] text-[#a9a396] hover:text-white hover:border-white/20'
                    : preference === 'minor'
                    ? 'bg-amber-950/40 border-amber-600/40 text-amber-300'
                    : 'bg-rose-950/40 border-rose-600/40 text-rose-300'
                }`}
                title="Spoiler-Filter Einstellungen"
              >
                {preference === 'none' ? (
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                )}
                <span className="hidden md:inline">{spoilerLabels[preference]}</span>
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>

              {isSpoilerMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-[#10121a] border border-white/15 rounded-xl shadow-2xl p-2 z-50 text-xs backdrop-blur-xl">
                  <div className="px-3 py-2 font-semibold text-white border-b border-white/[0.08] mb-1">
                    Spoiler-Filter
                  </div>
                  <button
                    onClick={() => { setPreference('none'); setIsSpoilerMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between transition-colors ${
                      preference === 'none' ? 'bg-[#1b1f2b] text-amber-300 font-semibold' : 'text-[#a9a396] hover:bg-white/[0.05] hover:text-white'
                    }`}
                  >
                    <span>Voll geschützt (Standard)</span>
                    {preference === 'none' && <span className="text-amber-400">✓</span>}
                  </button>
                  <button
                    onClick={() => { setPreference('minor'); setIsSpoilerMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between transition-colors ${
                      preference === 'minor' ? 'bg-[#1b1f2b] text-amber-300 font-semibold' : 'text-[#a9a396] hover:bg-white/[0.05] hover:text-white'
                    }`}
                  >
                    <span>Mittlere Spoiler erlauben</span>
                    {preference === 'minor' && <span className="text-amber-400">✓</span>}
                  </button>
                  <button
                    onClick={() => { setPreference('all'); setIsSpoilerMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between transition-colors ${
                      preference === 'all' ? 'bg-[#1b1f2b] text-amber-300 font-semibold' : 'text-[#a9a396] hover:bg-white/[0.05] hover:text-white'
                    }`}
                  >
                    <span>Alle Spoiler aufdecken</span>
                    {preference === 'all' && <span className="text-amber-400">✓</span>}
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu hamburger toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#a9a396] hover:text-white rounded-lg hover:bg-white/[0.05] transition-colors cursor-pointer"
              aria-label="Navigation umschalten"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-white/[0.08] bg-[#0c0e14] px-6 py-6 space-y-2 animate-fadeIn">
          {navItems.map((item) => {
            const isActive = location === item.href || (item.href !== '/' && location.startsWith(item.href));
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`block px-4 py-3 rounded-lg text-base font-heading font-semibold tracking-wide transition-all ${
                  isActive
                    ? 'bg-amber-950/30 text-amber-300 border-l-2 border-amber-400 pl-5'
                    : 'text-[#a9a396] hover:text-white hover:bg-white/[0.04]'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
};
